import React from 'react';
import { Sprout, TrendingUp, ArrowDownCircle, BarChart3, Scale, Shield } from 'lucide-react';

const Foundation = () => {
  return (
    <div className="bg-white relative">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-blue-50 to-transparent opacity-70" />
      <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(#113262 1px, transparent 1px)',
        backgroundSize: '30px 30px',
        opacity: 0.05
      }} />

      <div className="container relative z-10 max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6 lg:py-8">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="w-16 h-1 bg-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#113262] mb-4">
            Roots & Wings: The Foundation of Our Investment Philosophy
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Our approach blends the stability of strong businesses with the scalability of consistent growth, powered by proprietary technology and machine learning.
          </p>
        </div>

        {/* Main Content */}
        <div className="grid md:grid-cols-2 gap-10 mt-12">
          {/* Roots Section */}
          <div className="bg-gradient-to-br from-[#113262] to-[#1C52A0] rounded-xl p-8 text-white shadow-lg transform transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-center mb-6">
              <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mr-4">
                <Sprout className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold">Roots: For Stability & Strength</h3>
            </div>
            
            <p className="text-white/90 mb-6">
              We invest in businesses with strong foundations that can withstand economic storms and market volatility.
            </p>
            
            <div className="space-y-4">
              <Feature icon={<Scale className="w-5 h-5" />} title="Low or Zero Debt">
                Companies fueled by customer revenue and internal accruals, not excessive borrowing.
              </Feature>
              
              <Feature icon={<BarChart3 className="w-5 h-5" />} title="Strong Returns">
                Consistent high ROE/ROCE businesses that efficiently deploy capital.
              </Feature>
              
              <Feature icon={<Shield className="w-5 h-5" />} title="Promoter Integrity">
                Significant ownership by promoters who demonstrate skin-in-the-game and corporate governance.
              </Feature>
              
              <Feature icon={<ArrowDownCircle className="w-5 h-5" />} title="Resilience">
                Companies that have proven their ability to weather market cycles.
              </Feature>
            </div>
          </div>

          {/* Wings Section */}
          <div className="bg-gradient-to-br from-[#955C0A] to-[#F49611] rounded-xl p-8 text-white shadow-lg transform transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-center mb-6">
              <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mr-4">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold">Wings: For Growth & Outperformance</h3>
            </div>
            
            <p className="text-white/90 mb-6">
              We focus on companies with strong growth trajectories that can help your portfolio soar.
            </p>
            
            <div className="space-y-4">
              <Feature icon={<TrendingUp className="w-5 h-5" />} title="Accelerated Growth">
                Revenue and profit growth at 1.5x–3x the GDP rate, creating sustainable value.
              </Feature>
              
              <Feature icon={<BarChart3 className="w-5 h-5" />} title="Healthy Cash Flows">
                Strong operating cash flows that validate business model effectiveness.
              </Feature>
              
              <Feature icon={<Scale className="w-5 h-5" />} title="Pricing Power">
                Ability to maintain margins and pass on cost increases to customers.
              </Feature>
              
              <Feature icon={<ArrowDownCircle className="w-5 h-5" />} title="Recovery Potential">
                Companies that recover faster from downturns with consistent long-term potential.
              </Feature>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Feature Component for list items
const Feature = ({ icon, title, children }) => {
  return (
    <div className="flex">
      <div className="flex-shrink-0 text-white mr-3 mt-1">
        {icon}
      </div>
      <div>
        <h4 className="font-medium text-white">{title}</h4>
        <p className="text-white/80 text-sm mt-1">{children}</p>
      </div>
    </div>
  );
};

export default Foundation;